import { HexColorInput, HexColorPicker } from 'react-colorful';
import { Palette, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { controlClassName } from '@/components/form/fieldParts';
import { TEAM_COLOR_PRESETS, teamColorOf } from '@/components/teams/teamColors';
import { TeamTag } from '@/components/teams/TeamTag';

/**
 * Couleur du tag d'une équipe (#rrggbb), facultative : color picker (react-colorful) avec saisie hexadécimale et
 * raccourcis ; « Retirer » la vide (tag gris). Aperçu du tag avec le nom saisi.
 */
export function TeamColorField({
  value,
  onChange,
  name,
}: {
  value: string | null;
  onChange: (color: string | null) => void;
  /** Nom saisi, pour l'aperçu */
  name: string;
}) {
  const change = (color: string) => onChange(color.toLowerCase());

  return (
    <div className="space-y-1">
      <p id="team-color-label" className="text-xs font-semibold text-slate-500 uppercase">
        Couleur du tag
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Popover>
          <PopoverTrigger
            aria-labelledby="team-color-label team-color-value"
            className="inline-flex items-center gap-2 h-9 px-3 border border-slate-300 rounded-lg text-sm bg-card text-slate-700 outline-none hover:bg-slate-50 focus-visible:border-blue-500 focus-visible:ring-[3px] focus-visible:ring-blue-500/30"
          >
            {value ? (
              <span className="w-4 h-4 rounded-full border border-black/10" style={{ backgroundColor: value }} />
            ) : (
              <Palette className="w-4 h-4 text-slate-400" />
            )}
            <span id="team-color-value">{value ?? 'Aucune'}</span>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto space-y-3" aria-label="Choisir la couleur">
            <HexColorPicker color={teamColorOf(value ?? '')} onChange={change} />
            <HexColorInput
              color={value ?? ''}
              onChange={change}
              prefixed
              aria-label="Code hexadécimal"
              placeholder="#rrggbb"
              className={`${controlClassName} h-9 font-mono`}
            />
            <div className="grid grid-cols-5 gap-2">
              {TEAM_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset.color}
                  type="button"
                  aria-label={preset.label}
                  title={preset.label}
                  aria-pressed={value === preset.color}
                  onClick={() => change(preset.color)}
                  className="w-7 h-7 rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-blue-500/40 aria-pressed:ring-2 aria-pressed:ring-offset-2 aria-pressed:ring-slate-400"
                  style={{ backgroundColor: preset.color }}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <X className="w-3.5 h-3.5" />
            Retirer
          </button>
        )}
        {name.trim() && (
          <span className="ml-auto text-xs text-slate-500 flex items-center gap-2">
            Aperçu : <TeamTag name={name.trim()} color={value ?? ''} />
          </span>
        )}
      </div>
    </div>
  );
}
